import type { LayoutTile } from '../stage/layout.js'

/**
 * Paints the meeting onto a canvas that a MediaRecorder captures.
 *
 * The layout engine has laid the meeting out for THIS frame — not for the
 * recorder's window, whose stage is as wide as the browser and as tall as
 * it can afford (a band across a 16:9 canvas, dark above and below), but
 * for a 16:9 canvas of the recording's size, the way Meet's recording is
 * the meeting in a 16:9 frame and not a picture of somebody's window.
 * This only draws what it is handed: each participant's <video> — cover-fit
 * like the tile, or pillarboxed where the tile would pillarbox it, a
 * portrait phone — or their avatar; the presentation letterboxed; a name
 * on every tile. So the recording looks like the product, not like a grid
 * of raw streams.
 */

export interface CompositorSource {
  /** Where every tile is right now, in the recording's own pixels. */
  tiles(): LayoutTile[]
  /** A participant's live <video>, or null when their camera is off. */
  videoFor(peerId: string, kind: 'camera' | 'screen'): HTMLVideoElement | null
  nameFor(peerId: string): string
  /** For the avatar when the camera is off. */
  hueFor(peerId: string): number
  initialsFor(peerId: string): string
}

/** The frame a recording is made in: its size, its rate, and the bits spent on it. */
export interface RecordingProfile {
  width: number
  height: number
  frameRate: number
  videoBitsPerSecond: number
  audioBitsPerSecond: number
}

/**
 * The frame this machine can afford. Compositing and encoding happen in
 * the recorder's browser, beside the camera's simulcast layers and, when
 * they present, the share's encoder: 1080p is asked of a machine with
 * eight logical cores or more, 720p of anything smaller. VP9 needs fewer
 * bits per pixel on the larger frame and still comes out sharper.
 */
export function pickRecordingProfile(env: { cores: number }): RecordingProfile {
  // Fewer bits than before for the same picture: the frames now arrive
  // evenly (below) and the encoder is the hardware's, so nothing is spent
  // on judder. Speech needs no more than 96 kbps of Opus. The video made
  // from the recording is compressed again on the server at constant
  // quality, which is where most of the size goes.
  if (env.cores >= 8) {
    return {
      width: 1920,
      height: 1080,
      frameRate: 30,
      videoBitsPerSecond: 3_500_000,
      audioBitsPerSecond: 96_000,
    }
  }
  return {
    width: 1280,
    height: 720,
    frameRate: 30,
    videoBitsPerSecond: 2_000_000,
    audioBitsPerSecond: 96_000,
  }
}

/**
 * The most of a camera's picture that may be cropped away to fill its tile, along the side that
 * overflows: a person's picture loses little (a phone's face is in it from top to bottom), a
 * landscape one more (people sit in its middle). Beyond it the picture is not scaled up further;
 * what is left of the tile is the picture's own blur (see `CameraFrame.backdrop`), not bars.
 */
export const MAX_CROP = { portrait: 0.2, landscape: 0.6 } as const

/**
 * Where a cropped portrait picture is held: the face sits in the upper half of a phone's frame,
 * so what is cut is mostly below it.
 */
export const PORTRAIT_FOCUS_Y = 0.35

/** Where a camera's picture goes in its tile (the tile's own pixels), and whether it needs a backdrop. */
export interface CameraFrame {
  x: number
  y: number
  w: number
  h: number
  /** The picture does not reach every edge of the tile: draw its blurred copy behind. */
  backdrop: boolean
}

/**
 * A camera's picture in its tile. As large as fills the tile, unless that would crop away more
 * than MAX_CROP of it; then as large as that allows, the rest of the tile being a blurred copy.
 * A landscape webcam in a landscape tile, or a phone's picture in a tile shaped like a phone's,
 * fills it; a phone's picture in a landscape tile is shown nearly whole. A landscape webcam in a
 * tall tile (a phone's screen) is zoomed in, but at most 60% of its width goes: filling the
 * height cut away three quarters of it and the other person sat half out of the picture, and
 * stopping at 45% left bands the screen could not spare; the space that is left is the
 * picture's own blur, not bars. Null until the picture's or the tile's
 * size is known (the caller fills: object-fit: cover).
 */
/**
 * `maxScale` is how many of the box's pixels one pixel of the picture may be spread over, at most: it
 * stops a picture that arrived small (the connection could not carry more) from being blown up to
 * fill a tall screen, and shows it smaller and sharper instead. The picture is never smaller than
 * whole.
 */
export function cameraFrame(
  videoW: number,
  videoH: number,
  boxW: number,
  boxH: number,
  maxScale?: number,
): CameraFrame | null {
  if (!(videoW > 0 && videoH > 0 && boxW > 0 && boxH > 0)) return null
  const portrait = videoW < videoH
  const contain = Math.min(boxW / videoW, boxH / videoH)
  const cover = Math.max(boxW / videoW, boxH / videoH)
  // The side that overflows when filling: the one whose scale is the larger.
  const widthLeads = boxW / videoW >= boxH / videoH
  const kept = 1 - (portrait ? MAX_CROP.portrait : MAX_CROP.landscape)
  const capped = widthLeads ? boxH / (videoH * kept) : boxW / (videoW * kept)
  const limit = maxScale !== undefined && maxScale > 0 ? Math.min(capped, maxScale) : capped
  const scale = Math.min(cover, Math.max(contain, limit))
  const w = videoW * scale
  const h = videoH * scale
  return {
    x: (boxW - w) / 2,
    // Cropped at the top and bottom, a portrait picture is held by its face; otherwise centred.
    y: h > boxH && portrait ? (boxH - h) * PORTRAIT_FOCUS_Y : (boxH - h) / 2,
    w,
    h,
    backdrop: w < boxW - 1 || h < boxH - 1,
  }
}

/**
 * Whether a tile fills its box ('cover') or leaves some of it to the backdrop ('contain'); a
 * presentation is never cropped.
 */
export function fitFor(
  kind: 'camera' | 'screen',
  videoW: number,
  videoH: number,
  boxW?: number,
  boxH?: number,
): 'cover' | 'contain' {
  if (kind === 'screen') return 'contain'
  const frame = cameraFrame(videoW, videoH, boxW ?? 0, boxH ?? 0)
  return frame?.backdrop ? 'contain' : 'cover'
}
export interface Compositor {
  readonly stream: MediaStream
  readonly canvas: HTMLCanvasElement
  stop(): void
}

export function createCompositor(source: CompositorSource, profile: RecordingProfile): Compositor {
  const { width, height, frameRate } = profile
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) throw new Error('canvas 2d unavailable')
  // A 1080p share drawn into a smaller box, or a 360p face into a larger
  // one: the better resampler is worth its cost at 30 frames a second.
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  let stopped = false

  const draw = (): void => {
    if (stopped) return
    ctx.fillStyle = '#0f172a'
    ctx.fillRect(0, 0, width, height)

    for (const tile of source.tiles()) {
      const { x, y, w, h } = tile
      if (w < 2 || h < 2) continue

      ctx.save()
      roundedRect(ctx, x, y, w, h, 8)
      ctx.clip()
      ctx.fillStyle = tile.kind === 'screen' ? '#000000' : '#1e293b'
      ctx.fillRect(x, y, w, h)

      const video = source.videoFor(tile.peerId, tile.kind)
      if (video && tile.showVideo && video.readyState >= 2 && video.videoWidth > 0) {
        const frame =
          tile.kind === 'camera' ? cameraFrame(video.videoWidth, video.videoHeight, w, h) : null
        if (frame) {
          if (frame.backdrop) drawBackdrop(ctx, video, x, y, w, h)
          ctx.drawImage(video, x + frame.x, y + frame.y, frame.w, frame.h)
        } else {
          drawContain(ctx, video, x, y, w, h)
        }
      } else if (tile.kind === 'camera') {
        drawAvatar(ctx, source.initialsFor(tile.peerId), source.hueFor(tile.peerId), x, y, w, h)
      }

      // The name bar, as the tile draws it.
      const label =
        tile.kind === 'screen'
          ? `${source.nameFor(tile.peerId)} is presenting`
          : source.nameFor(tile.peerId)
      const barH = Math.max(18, Math.min(40, h * 0.1))
      const grad = ctx.createLinearGradient(0, y + h - barH * 2, 0, y + h)
      grad.addColorStop(0, 'rgba(0,0,0,0)')
      grad.addColorStop(1, 'rgba(0,0,0,0.7)')
      ctx.fillStyle = grad
      ctx.fillRect(x, y + h - barH * 2, w, barH * 2)
      ctx.fillStyle = '#ffffff'
      ctx.font = `${Math.round(barH * 0.55)}px system-ui, sans-serif`
      ctx.textBaseline = 'middle'
      ctx.fillText(label, x + barH * 0.4, y + h - barH * 0.55, w - barH * 0.8)
      ctx.restore()
    }
  }

  // A steady clock rather than requestAnimationFrame: a background tab
  // throttles rAF to nothing, and the recording must keep its frame rate
  // while the presenter looks at what they are sharing. Each painted frame
  // is handed to the encoder as it is finished (captureStream(0) and
  // requestFrame), rather than sampled on the capture's own clock, which
  // caught half-painted frames and dropped others.
  const stream = canvas.captureStream(0)
  const track = stream.getVideoTracks()[0] as
    (MediaStreamTrack & { requestFrame?: () => void }) | undefined
  const paint = (): void => {
    draw()
    track?.requestFrame?.()
  }
  const timer = setInterval(paint, Math.round(1000 / frameRate))
  paint()

  return {
    stream,
    canvas,
    stop() {
      stopped = true
      clearInterval(timer)
      for (const t of stream.getTracks()) t.stop()
    },
  }
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

/** Fill the box, cropping — object-fit: cover. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const vw = video.videoWidth
  const vh = video.videoHeight
  const scale = Math.max(w / vw, h / vh)
  const dw = vw * scale
  const dh = vh * scale
  ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
}

/** The size the backdrop's picture is shrunk to: stretched back up, it is a blur, for almost no work. */
const BACKDROP_SIZE = 24
let backdropCanvas: HTMLCanvasElement | null = null

/**
 * Behind a picture shown whole: the same picture, filled and blurred (shrunk, then stretched with
 * smoothing, which works where a canvas filter does not), and dimmed a little.
 */
function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  backdropCanvas ??= document.createElement('canvas')
  const small = backdropCanvas
  const aspect = w / h
  small.width = aspect >= 1 ? BACKDROP_SIZE : Math.max(2, Math.round(BACKDROP_SIZE * aspect))
  small.height = aspect >= 1 ? Math.max(2, Math.round(BACKDROP_SIZE / aspect)) : BACKDROP_SIZE
  const sctx = small.getContext('2d')
  if (!sctx) return
  sctx.imageSmoothingEnabled = true
  drawCover(sctx, video, 0, 0, small.width, small.height)
  ctx.drawImage(small, x, y, w, h)
  ctx.fillStyle = 'rgba(0,0,0,0.25)'
  ctx.fillRect(x, y, w, h)
}

/** Fit inside the box, letterboxing — object-fit: contain. */
function drawContain(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const vw = video.videoWidth
  const vh = video.videoHeight
  const scale = Math.min(w / vw, h / vh)
  const dw = vw * scale
  const dh = vh * scale
  ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
}

function drawAvatar(
  ctx: CanvasRenderingContext2D,
  initials: string,
  hue: number,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const r = Math.min(w, h) * 0.18
  const cx = x + w / 2
  const cy = y + h / 2
  ctx.fillStyle = `hsl(${hue} 45% 42%)`
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.font = `600 ${Math.round(r * 0.9)}px system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(initials, cx, cy + r * 0.05)
  ctx.textAlign = 'start'
}
